import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { useState } from 'react';
import getStateManager from '../StateManager.js';
import { Route, useParams, Link, Switch, Redirect } from 'react-router-dom';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import { AppBar, makeStyles, Tab } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AppsIcon from '@material-ui/icons/Apps';
import { CircularIndeterminate } from '../FormWidgets/Others';

import { useSnapshot } from 'valtio';

import { Error404 } from '../components/Error404';
import { ResponsiveActionButton } from '../components/ResponsiveActionButton';
import { useIsMobile } from '../Hooks/useMedia';
import { useVisibleGlobalAhu } from '../Hooks/useVisibleGlobalAhu';

import SettingsIcon from '@material-ui/icons/Settings';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from 'react-i18next';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';

import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';

import { SettingsTabs } from '../Pages/ControllerPage/SettingsTabs.js';
import SystemSettings from '../Tabs/SystemSettings.js';
import FlexAlarmSettings from '../Tabs/FlexAlarmSettings.js';
import MultiOutSettings from '../Tabs/MultiOutSettings';
import Slaves from '../Tabs/Industrial/Slaves.js';
import OverwriteIOSettings from '../Tabs/OverwriteIOSettings';
import MultiIOUserFunctionSet from '../Tabs/Industrial/MultiIOUserFunctionSet.js';
import EquipmentSettings from '../Tabs/EquipmentSettings.js';
import ZoneOverview from '../Tabs/Industrial/ZoneOverview.js';
import MultipleNutrientSettings from '../Tabs/NutrientSettings';
import { hasTankSettings } from '../Utils/nutrition_utils';
import { IsExcludedBasedOnTypeOrProject } from '../Utils/utils';

import _ from 'lodash';

const useStyles = makeStyles((theme) => ({
  withDivider: {
    borderRight: `1px solid ${theme.palette.text.primary}`
  },
  paperBackground: {
    backgroundColor: theme.palette.background.default
  },
  globalSettingsTabs: {
    margin: '24px 24px 0',
    width: 'calc(100% - 48px)',
    [theme.breakpoints.down('sm')]: {
      margin: `${theme.spacing(1)}px 0 0`,
      width: '100%'
    }
  }
}));

export default function ZoneMenu(props) {
  const mobile = useIsMobile(960);
  let { id, menu = 'zones', zone_id = 'zone_0', tab_id = 'info' } = useParams();
  const stateManager = getStateManager();
  const _controller = stateManager.getControllerById(id);
  const controller = useSnapshot(_controller);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(_controller);

  const visibleGlobalAhu = useVisibleGlobalAhu(_controller);
  const showGlobalIO = controller.access_rights?.multi_io?.read === true;
  const showGlobalOverwrites = showGlobalIO &&
    !IsExcludedBasedOnTypeOrProject(controller, 'overwr_in_out', 'tabs');
  const showGlobalNutrition = hasTankSettings(controller.settings?.nutrition) &&
    controller.access_rights?.nutrition?.read === true &&
    !IsExcludedBasedOnTypeOrProject(controller, 'nutrition', 'tabs');

  let { t } = useTranslation();

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [renameDialog, setRenameDialog] = useState(null); // null => closed, otherwise set to zone object
  const [deleteDialog, setDeleteDialog] = useState(null); // null => closed, otherwise set to zone object
  const [initialName, setInitialName] = useState('');

  const classes = useStyles();
  const panelValue = menu === 'zones' ? zone_id : menu;
  const mainTabValues = [
    'zoneoverview',
    'manage_zones',
    ...Object.values(controller?.zones ?? {}).map((zone) => `zone_${zone.id}`)
  ];
  const mainTabValue = mainTabValues.includes(panelValue) ? panelValue : 'manage_zones';

  return (
    <>
      {/* !zone_id && <Redirect to={`${url}/zones/${Object.values (controller?.zones ?? {})[0]?.name ?? 'default'}/info`} /> */}
      <TabContext value={panelValue}>
        {!_.isEmpty(controller?.zones) && (
          <>
            <TabContext value={mainTabValue}>
              <AppBar position="static" color="default">
                <TabList variant="scrollable" scrollButtons="auto">
                  {[
                    <Tab
                      value="zoneoverview"
                      label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <AppsIcon /> {t('overview')}
                        </div>
                      }
                      component={Link}
                      className={classes.withDivider}
                      to={`/controller/${controller.id}/zoneoverview`}
                      key={`overview-tab`}
                      id={`overview-tab`}
                      aria-controls={`overview-tabpanel`}
                      /* due to this bug we cannot use conditional rendering inside TabList
                so we use this hack to hide the tab when only one zone is present
                https://github.com/mui/material-ui/issues/26166
              */
                      style={{
                        display:
                          visibleGlobalAhu || Object.keys(controller.zones).length > 1
                            ? 'flex'
                            : 'none'
                      }}
                    />,

                    ...Object.values(controller.zones).map((zone, index) => (
                      <Tab
                        value={'zone_' + zone.id}
                        label={zone.name}
                        component={Link}
                        className={
                          index === Object.keys(controller.zones).length - 1
                            ? classes.withDivider
                            : ''
                        }
                        to={`/controller/${id}/zones/zone_${zone.id}/${tab_id ?? 'info'}`}
                        key={`zone_${zone.id}`}
                        id={`zone_${zone.id}`}
                        aria-controls={`zone-tabpanel-${index}`}
                      />
                    )),

                    <Tab
                      value="manage_zones"
                      label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <SettingsIcon />
                        </div>
                      }
                      component={Link}
                      className={`${classes.actionTab}`}
                      to={`/controller/${id}/manage_zones`}
                      key={`manage_zones-tab`}
                      id={`manage_zones-tab`}
                      aria-controls={`manage_zones-tabpanel`}
                    />
                  ]}
                </TabList>
              </AppBar>
            </TabContext>

            {[
              'manage_zones',
              'slaves',
              'equipment',
              'global_ios',
              'overwr_in_out',
              'user_func_sets',
              'nutrition',
              'alarms',
              'system'
            ].includes(menu) && (
              <TabContext value={menu}>
                <AppBar position="static" color="default" className={classes.globalSettingsTabs}>
                  <Box display="flex" justifyContent="center" width="100%">
                    <TabList variant="scrollable" scrollButtons="auto">
                      <Tab
                        value="manage_zones"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('manage_zones')}
                          </div>
                        }
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/manage_zones`}
                        key={`manage_zones-tab`}
                        id={`manage_zones-tab`}
                        aria-controls={`manage_zones-tabpanel`}
                      />

                      <Tab
                        value="slaves"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('manage_slaves')}
                          </div>
                        }
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/slaves`}
                        key={`manage_slaves-tab`}
                        id={`manage_slaves-tab`}
                        aria-controls={`manage_slaves-tabpanel`}
                      />

                      <Tab
                        value="equipment"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('equipment')}
                          </div>
                        }
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/equipment`}
                        key={`equipment-tab`}
                        id={`equipment-tab`}
                        aria-controls={`equipment-tabpanel`}
                      />

                      <Tab
                        value="global_ios"
                        style={{ display: showGlobalIO ? 'flex' : 'none' }}
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('global_ios')}
                          </div>
                        }
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/global_ios`}
                        key={`global_ios-tab`}
                        id={`global_ios-tab`}
                        aria-controls={`global_ios-tabpanel`}
                      />

                      <Tab
                        value="overwr_in_out"
                        label={t('overwr_in_out')}
                        component={Link}
                        to={`/controller/${id}/overwr_in_out`}
                        id="global-overwrites-tab"
                        aria-controls="global-overwrites-tabpanel"
                        style={{ display: showGlobalOverwrites ? 'flex' : 'none' }}
                      />

                      <Tab
                        value="user_func_sets"
                        style={{ display: showGlobalIO ? 'flex' : 'none' }}
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {t('user_func_sets')}
                          </div>
                        }
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/user_func_sets`}
                        key={`user_func_sets-tab`}
                        id={`user_func_sets-tab`}
                        aria-controls={`user_func_sets-tabpanel`}
                      />

                      <Tab
                        value="nutrition"
                        label={t('nutrition')}
                        component={Link}
                        to={`/controller/${id}/nutrition`}
                        id="global-nutrition-tab"
                        aria-controls="global-nutrition-tabpanel"
                        style={{ display: showGlobalNutrition ? 'flex' : 'none' }}
                      />

                      <Tab
                        value="alarms"
                        label={t('alarms')}
                        component={Link}
                        className={`${classes.actionTab}`}
                        to={`/controller/${id}/alarms`}
                        key="global-alarms-tab"
                        id="global-alarms-tab"
                        aria-controls="global-alarms-tabpanel"
                        style={{ display: controller.api_version >= 'BAI' ? 'flex' : 'none' }}
                      />

                      <Tab
                        value="system"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>{t('system')}</div>
                        }
                        component={Link}
                        className={classes.actionTab}
                        to={`/controller/${id}/system`}
                        key={`system-tab`}
                        id={`system-tab`}
                        aria-controls={`system-tabpanel`}
                      />
                    </TabList>
                  </Box>
                </AppBar>
              </TabContext>
            )}
          </>
        )}

        {props.recoveryMode && (
          <>
            <TabContext value="manage_zones">
              <AppBar position="static" color="default">
                <TabList variant="scrollable" scrollButtons="auto">
                  {[
                    <Tab
                      value="manage_zones"
                      label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <SettingsIcon />
                        </div>
                      }
                      component={Link}
                      className={`${classes.actionTab}`}
                      to={`/controller/${id}/manage_zones`}
                      key={`manage_zones-tab`}
                      id={`manage_zones-tab`}
                      aria-controls={`manage_zones-tabpanel`}
                    />
                  ]}
                </TabList>
              </AppBar>
            </TabContext>

            {['manage_zones', 'slaves', 'equipment', 'global_ios', 'system'].includes(menu) && (
              <TabContext value="system">
                <AppBar position="static" color="default" className={classes.globalSettingsTabs}>
                  <Box display="flex" justifyContent="center" width="100%">
                    <TabList variant="scrollable" scrollButtons="auto">
                      {[
                        <Tab
                          value="system"
                          label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {t('system')}
                            </div>
                          }
                          component={Link}
                          className={classes.actionTab}
                          to={`/controller/${id}/system`}
                          key={`system-tab`}
                          id={`system-tab`}
                          aria-controls={`system-tabpanel`}
                        />
                      ]}
                    </TabList>
                  </Box>
                </AppBar>
              </TabContext>
            )}
          </>
        )}

        <Switch>
          {!_.isEmpty(controller?.zones) && (
            <>
              <Route path={`/controller/:id/slaves`}>
                <TabPanel value="slaves">
                  {mobile ? (
                    <Paper
                      style={{
                        padding: '1em',
                        margin: '0em',
                        marginLeft: '-1em',
                        marginRight: '-1em'
                      }}
                    >
                      <Slaves />
                    </Paper>
                  ) : (
                    <Container>
                      <Paper style={{ padding: '2em' }}>
                        <Slaves />
                      </Paper>
                    </Container>
                  )}
                </TabPanel>
              </Route>
              <Route exact path={`/controller/:id/global_ventilation`}>
                <Redirect to={`/controller/${id}/equipment`} />
              </Route>
              <Route path={`/controller/:id/equipment`}>
                <TabPanel value="equipment">
                  {controller?.slaves ? (
                    <Container>
                      <Paper className={classes.paperBackground} elevation={0}>
                        <EquipmentSettings />
                      </Paper>
                    </Container>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
                      <CircularIndeterminate />
                    </div>
                  )}
                </TabPanel>
              </Route>
              {showGlobalNutrition && <Route path={`/controller/:id/nutrition`}>
                <TabPanel value="nutrition" id="global-nutrition-tabpanel">
                  <Container><MultipleNutrientSettings /></Container>
                </TabPanel>
              </Route>}
              {showGlobalIO && <Route path={`/controller/:id/global_ios`}>
                <TabPanel value="global_ios">
                  {controller?.slaves ? (
                    <Container>
                      <Paper className={classes.paperBackground} elevation={0}>
                        <MultiOutSettings controller={controller} />
                      </Paper>
                    </Container>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
                      <CircularIndeterminate />
                    </div>
                  )}
                </TabPanel>
              </Route>
              }
              {showGlobalOverwrites && (
                <Route path={`/controller/:id/overwr_in_out`}>
                  <TabPanel value="overwr_in_out" id="global-overwrites-tabpanel">
                    {controller?.slaves && controller?.overwr_io ? (
                      <Container>
                        <OverwriteIOSettings />
                      </Container>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
                        <CircularIndeterminate />
                      </div>
                    )}
                  </TabPanel>
                </Route>
              )}
              {showGlobalIO && <Route path={`/controller/:id/user_func_sets`}>
                <TabPanel value="user_func_sets">
                  {controller?.slaves ? (
                    <Container>
                      <Paper className={classes.paperBackground} elevation={0}>
                        <MultiIOUserFunctionSet controller={controller} />
                      </Paper>
                    </Container>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
                      <CircularIndeterminate />
                    </div>
                  )}
                </TabPanel>
              </Route>
              }
              <Route path={`/controller/:id/zoneoverview`}>
                <TabPanel value="zoneoverview">
                  <Container>
                    <ZoneOverview controller={_controller} />
                  </Container>
                </TabPanel>
              </Route>
              <Route path={`/controller/:id/system`}>
                <TabPanel value="system">
                  {controller?.settings?.system ? (
                    <Container>
                      <Paper className={classes.paperBackground} elevation={0}>
                        <SystemSettings
                          controller={controller}
                          parent={{ ...controller, id: '254' }}
                        />
                      </Paper>
                    </Container>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3em' }}>
                      <CircularIndeterminate />
                    </div>
                  )}
                </TabPanel>
              </Route>
              {controller.api_version >= 'BAI' && (
                <Route path={`/controller/:id/alarms`}>
                  <TabPanel value="alarms">
                    <Container>
                      <FlexAlarmSettings />
                    </Container>
                  </TabPanel>
                </Route>
              )}
              <Route path={`/controller/:id/manage_zones`}>
                <TabPanel value="manage_zones">
                  <Container>
                    <TableContainer component={Paper}>
                      <Table aria-label="table of zones">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.values(controller.zones).map((zone) => (
                            <TableRow key={zone.id}>
                              <TableCell>
                                {zone.name === '' ? '--- (' + zone.id + ')' : zone.name}
                              </TableCell>
                              <TableCell align="right" padding={mobile ? 'none' : 'normal'}>
                                <ResponsiveActionButton
                                  title={connectionDisabledReason}
                                  disabled={connectionStatus !== 'good'}
                                  compact={mobile}
                                  icon={<EditIcon />}
                                  label={t('rename')}
                                  onClick={() => {
                                    setInitialName(zone.name);
                                    setRenameDialog(zone);
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right" padding={mobile ? 'none' : 'normal'}>
                                <ResponsiveActionButton
                                  title={connectionDisabledReason}
                                  compact={mobile}
                                  icon={<DeleteIcon />}
                                  label={t('delete')}
                                  onClick={() => setDeleteDialog(zone)}
                                  color="secondary"
                                  disabled={
                                    connectionStatus !== 'good' ||
                                    Object.values(controller.zones).length <= 1
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <ResponsiveActionButton
                      title={connectionDisabledReason}
                      disabled={connectionStatus !== 'good'}
                      compact={mobile}
                      icon={<AddIcon />}
                      label={t('add_zone')}
                      onClick={() => setOpenAddDialog(true)}
                    />
                  </Container>

                  <AddDialog
                    open={openAddDialog}
                    controller={_controller}
                    wsManager={stateManager.wsManager}
                    handleClose={() => setOpenAddDialog(false)}
                  />
                  <RenameDialog
                    zone={renameDialog}
                    name={initialName}
                    setName={setInitialName}
                    controller={_controller}
                    wsManager={stateManager.wsManager}
                    handleClose={() => setRenameDialog(null)}
                  />
                  <DeleteDialog
                    zone={deleteDialog}
                    controller={_controller}
                    wsManager={stateManager.wsManager}
                    handleClose={() => setDeleteDialog(null)}
                  />
                </TabPanel>
              </Route>

              {Object.values(controller.zones).map((zone, index) => (
                <TabPanel
                  key={`zone-panel-${zone.id}`}
                  value={'zone_' + zone.id}
                  style={{ padding: 0 }}
                >
                  <SettingsTabs
                    path={`/controller/:id/:zones(zones)?/:zone_id(zone_${zone.id})?`}
                    url={`/controller/${id}/zones/zone_${zone.id}`}
                    controller={controller}
                    parent={controller.zones[index]}
                  />
                </TabPanel>
              ))}
            </>
          )}

          {props.recoveryMode && (
            <Route path={`/controller/:id/system`}>
              <TabPanel value="system">
                <Container>
                  <Paper className={classes.paperBackground} elevation={0}>
                    <SystemSettings controller={controller} recoveryMode={true} />
                  </Paper>
                </Container>
              </TabPanel>
            </Route>
          )}

          <Route path="*">
            <Error404 />
          </Route>
        </Switch>
      </TabContext>
    </>
  );
}

function AddDialog({ open, handleClose, controller, wsManager }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const [newZoneName, setNewZoneName] = useState('');
  const action = () => {
    if (controller.connection?.status !== 'good') return;
    wsManager.sendWSMsg(
      controller,
      'add_zone',
      {
        id: '-1',
        name: newZoneName
      },
      false
    );
    setNewZoneName('');
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={open}
      onClose={handleClose}
      onEnterAction={action}
      aria-labelledby="form-dialog-add"
    >
      <DialogTitle id="form-dialog-add">{t('add_zone')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          onChange={(evt) => setNewZoneName(evt.target.value)}
          value={newZoneName}
          id="zone_name"
          label={t('name')}
          type="text"
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={action}
          style={{ marginTop: '1em' }}
          variant="contained"
          type="button"
        >
          {t('save')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function RenameDialog({ zone, name, setName, handleClose, controller, wsManager }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const action = () => {
    if (controller.connection?.status !== 'good') return;
    wsManager.sendWSMsg(controller, 'rename_zone', {
      id: zone.id,
      name: name
    });
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={zone != null}
      onClose={handleClose}
      onEnterAction={action}
      aria-labelledby="form-dialog-rename"
    >
      <DialogTitle id="form-dialog-rename">{t('rename')}</DialogTitle>
      <DialogContent>
        <TextField
          value={name}
          autoFocus
          margin="dense"
          id="rename"
          label="Name"
          type="text"
          fullWidth
          onChange={(event) => setName(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={action}
          color="primary"
        >
          {t('rename')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function DeleteDialog({ zone, handleClose, controller, wsManager }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const action = () => {
    if (controller.connection?.status !== 'good') return;
    wsManager.sendWSMsg(controller, 'delete_zone', {
      id: zone.id
    });
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={zone != null}
      onClose={handleClose}
      onEnterAction={action}
      aria-labelledby="form-dialog-delete"
    >
      <DialogTitle id="form-dialog-delete">{t('delete')}</DialogTitle>
      <DialogContent>{t('confirm_delete')}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={action}
          color="primary"
        >
          {t('yes')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
